import { Layer, ObjectCategory } from "@common/constants";
import { JumpIn } from "@common/definitions/jumpIn";
import { type MapData } from "@common/packets/mapPacket";
import { type CommonObjectMapping } from "@common/utils/gameObject";
import { CircleHitbox, GroupHitbox, RectangleHitbox, type Hitbox, type ShapeHitbox } from "@common/utils/hitbox";
import { inReach, landingGuess, nearestOpen, settleReach, steer, wetGround } from "@common/utils/jumpIn";
import { adjacentOrEquivLayer } from "@common/utils/layer";
import { Numeric } from "@common/utils/math";
import { type Terrain } from "@common/utils/terrain";
import { Vec, type Vector } from "@common/utils/vector";

/**
 * The jump-in's page (1.06, managers/jumpIn.ts), the parts without Pixi (tests/src/jumpIn1061.test.ts): the ground under
 * a chute's ring (1.06.1), where the page says a chute may come down as the server judges it (server/src/jumpIn.ts
 * `JumpInManager.isOpen`): not on a roof (a building's ceiling, where the server's scope is, or its walls), the water,
 * or a solid; but on a roof's deck, and through a roof's glass (1.08, server/src/roofs.ts). The roofs, the decks and the
 * glass are every building's of the map's, from the map (off the screen too); the solids are the obstacles the page has
 * been sent (the server knows the rest of the map's).
 */

/** a building's roof and walls on the ground floor: its ceiling (none for a canopy without the server's scope), its walls, round both */
export interface RoofView {
    readonly scope?: Hitbox
    readonly walls?: Hitbox
    readonly bounds: RectangleHitbox
}

/**
 * the ground under a chute's aim: open, a roof's deck (1.08), a roof's glass (1.08: come down through it), a roof (a
 * building), the water, or a solid (an obstacle)
 */
export type GroundKind = "open" | "deck" | "glass" | "roof" | "water" | "solid";

/** where a chute may come down for a player, as the server's `JumpInManager._landable`: open ground or a roof's deck */
export const landable = (ground: GroundKind): boolean => ground === "open" || ground === "deck";

/** a roof's deck (1.08, `BuildingDefinition.openAir`): its floor's layer, where one stands on it, its parapet (its walls) */
export interface DeckView {
    readonly layer: number
    readonly floor: Hitbox
    readonly walls?: Hitbox
    readonly bounds: RectangleHitbox
}

/**
 * the roofs one comes down on or through (1.08): the map's decks, the panes of its roofs' glass (whole or smashed: a
 * chute aimed at one comes down through it), and the solids on the decks this page has been sent (a deck's units, a
 * railing, a flight's opening: `solidOnDeck`), by their floor
 */
export interface RoofTops {
    readonly decks: readonly DeckView[]
    readonly glass: readonly Hitbox[]
    readonly solids: readonly DeckSolid[]
}

/** a solid on a deck (`deckSolid`), as the ring needs it: where it is, its floor, and whether it's a flight's opening */
export interface DeckSolid {
    readonly hitbox?: Hitbox
    readonly layer: number
    readonly definition?: { readonly isStair?: boolean, readonly toDeck?: boolean }
}

/**
 * whether a solid on a deck (`deckSolid`) is in a chute's way on a deck on `layer`: one on its floor, or the opening of
 * a flight of stairs up onto it, a floor under it (0.98.2, `toDeck`: the blocks' roof stairs), as the server's `deckAt`
 * and `flightOpeningAt`
 */
export const solidOnDeck = (solid: DeckSolid, layer: number): boolean =>
    solid.layer === layer || (solid.definition?.isStair === true && solid.definition.toDeck === true && solid.layer === layer - 1);

/** no decks nor glass: the ground floor only, as before 1.08 (a practice bot's, the tests') */
export const NO_ROOF_TOPS: RoofTops = { decks: [], glass: [], solids: [] };

/**
 * the map's roofs and walls on the ground floor (and the floors by it, as the server's grid looks), as the server's
 * `Building` makes its scope (the ceiling, unless `noCeilingScopeEffect`) and its walls
 */
export function mapRoofs(objects: MapData["objects"]): RoofView[] {
    const list: RoofView[] = [];
    for (const object of objects) {
        if (object.type !== ObjectCategory.Building || !adjacentOrEquivLayer(object, Layer.Ground)) continue;
        const { definition, position, orientation } = object;
        const scope = definition.noCeilingScopeEffect ? undefined : definition.ceilingHitbox?.transform(position, 1, orientation);
        const walls = definition.hitbox?.transform(position, 1, orientation);
        const boxes = [scope, walls].filter(hitbox => hitbox !== undefined).map(hitbox => hitbox.toRectangle());
        if (boxes.length === 0) continue;
        const bounds = new RectangleHitbox(
            Vec(Math.min(...boxes.map(box => box.min.x)), Math.min(...boxes.map(box => box.min.y))),
            Vec(Math.max(...boxes.map(box => box.max.x)), Math.max(...boxes.map(box => box.max.y)))
        );
        list.push({ scope, walls, bounds });
    }
    return list;
}

/**
 * the map's roofs' decks and glass (1.08), as the server's `Building` places them (`deckFloor`, `glass`): every deck of
 * the map's and every pane, off the screen too (the solids on the decks are the page's: `deckSolid`)
 */
export function mapRoofTops(objects: MapData["objects"]): Pick<RoofTops, "decks" | "glass"> {
    const decks: DeckView[] = [];
    const glass: Hitbox[] = [];
    for (const object of objects) {
        if (object.type !== ObjectCategory.Building) continue;
        const { definition, position, orientation, layer } = object;
        for (const pane of definition.glass ?? []) glass.push(pane.hitbox.transform(position, 1, orientation));
        if (!definition.openAir || !definition.floors?.length) continue;
        const floors = definition.floors.map(({ hitbox }) => hitbox.transform(position, 1, orientation));
        const floor = floors.length === 1
            ? floors[0]
            : new GroupHitbox(...floors.flatMap(hitbox => hitbox instanceof GroupHitbox ? hitbox.hitboxes : [hitbox as ShapeHitbox]));
        const walls = definition.hitbox?.transform(position, 1, orientation);
        decks.push({ layer, floor, walls, bounds: floor.toRectangle() });
    }
    return { decks, glass };
}

/** an obstacle on a roof's deck a chute can't come down on (1.08), as the server's `solidOn`: on a floor above the ground */
export const deckSolid = (obstacle: CommonObjectMapping[ObjectCategory.Obstacle]): boolean =>
    !obstacle.dead
    && obstacle.layer > Layer.Ground
    && (!obstacle.definition.noCollisions || obstacle.definition.isStair === true || obstacle.definition.trap !== undefined);

/**
 * an obstacle a chute can't come down on, as the server's: standing, and solid, stairs or a trap, on the ground floor or
 * by it
 */
export const landingSolid = (obstacle: CommonObjectMapping[ObjectCategory.Obstacle]): boolean =>
    !obstacle.dead
    && adjacentOrEquivLayer(obstacle, Layer.Ground)
    && (!obstacle.definition.noCollisions || obstacle.definition.isStair === true || obstacle.definition.trap !== undefined);

/**
 * the ground at `point` to a player of `radius` coming down there, as the server judges it (`JumpInManager.isOpen`; 1.08,
 * a roof's glass first, as its `glassAt`, then a deck, as its `deckAt`: a player's)
 */
export function groundAt(
    point: Vector,
    radius: number,
    roofs: readonly RoofView[],
    solids: ReadonlyArray<{ readonly hitbox?: Hitbox }>,
    terrain: Pick<Terrain, "getFloor">,
    tops: RoofTops = NO_ROOF_TOPS
): GroundKind {
    const circle = new CircleHitbox(radius + 0.75, point);
    if (tops.glass.length > 0) {
        const dot = new CircleHitbox(0.5, point);
        for (const pane of tops.glass) {
            if (pane.collidesWith(dot)) return "glass";
        }
    }
    for (const deck of tops.decks) {
        if (!deck.bounds.isPointInside(point) || !deck.floor.isPointInside(point)) continue;
        if (deck.walls?.collidesWith(circle)) continue;
        if (tops.solids.some(solid => solidOnDeck(solid, deck.layer) && solid.hitbox?.collidesWith(circle))) continue;
        return "deck";
    }
    for (const roof of roofs) {
        if (!roof.bounds.collidesWith(circle)) continue;
        if (roof.scope?.collidesWith(circle) || roof.walls?.collidesWith(circle)) return "roof";
    }
    if (wetGround(terrain, point, radius)) return "water";
    for (const solid of solids) {
        if (solid.hitbox?.collidesWith(circle)) return "solid";
    }
    return "open";
}

/** the map as the ring needs it: its size, and its ground (the water) */
export interface RingMap {
    readonly width: number
    readonly height: number
    readonly terrain: Pick<Terrain, "getFloor">
}

/**
 * the ring: where a chute steered as it's steered now is aimed, the ground there, and where it comes down; `settling`,
 * the aim isn't open ground with room round it (the server's final approach takes the chute to `spot`)
 */
export interface RingPlace {
    readonly aim: Vector
    readonly ground: GroundKind
    readonly spot: Vector
    readonly settling: boolean
}

/**
 * Where the page's ring goes (1.06.1): where the server's chute comes down, steered as it's steered now. Aimed at open
 * ground, there; at a roof, the water or a solid, the open ground nearest the aim that the chute can glide to in its
 * final approach (the server's `JumpInManager.settleSpot`), or, none being in reach, the nearest to where its drift takes
 * it. The reach is the server's: in the final approach (the last `JumpIn.settleMs`), from where the chute is now, as the
 * server takes it each tick; before that, from where steering as now takes the chute by the approach's start, the spot
 * the server will look for then (1.06.1's second review: from the whole fall's reach, the ring showed the far side of the
 * museum's hall to a chute steered across it, and the server took it to the near side, 150 units off, the ring jumping
 * there in its last 4 s). Kept as the server keeps its spot: while it's in reach (or none was), the aim stays by it or
 * comes on toward it, and it's open ground still; looked for again at most every `searchMs`.
 */
export class LandingRing {
    private _settle?: { readonly aim: Vector, readonly spot?: Vector, readonly at: number, readonly beyond: boolean };

    constructor(private readonly _searchMs: number) {}

    /** the spot the ring is on, aimed at a roof or the water (the lab reads it) */
    get settle(): { readonly aim: Vector, readonly spot?: Vector } | undefined {
        return this._settle;
    }

    reset(): void {
        this._settle = undefined;
    }

    /**
     * the ring of a chute of `radius` at `position`, drifting `velocity`, steered `direction` (`steerDirection`), `left`
     * ms from the ground, at `now` (ms): the map's roofs (`mapRoofs`), the solids known (`landingSolid`) and the roofs one
     * comes down on or through (1.08: `mapRoofTops`, `deckSolid`)
     */
    find(
        now: number,
        position: Vector,
        velocity: Vector,
        direction: Vector,
        left: number,
        radius: number,
        map: RingMap,
        roofs: readonly RoofView[],
        solids: ReadonlyArray<{ readonly hitbox?: Hitbox }>,
        tops: RoofTops = NO_ROOF_TOPS
    ): RingPlace {
        const { width, height, terrain } = map;
        const margin = radius + 1;
        // where it's aimed: where it comes down steered as now (inside the map, as the server keeps it)
        const aim = landingGuess(position, velocity, direction, left);
        aim.x = Numeric.clamp(aim.x, margin, width - margin);
        aim.y = Numeric.clamp(aim.y, margin, height - margin);
        const ground = groundAt(aim, radius, roofs, solids, terrain, tops);
        // (open ground or a deck with room to spare round it, as the server's: `JumpIn.settleClearance`; a roof's glass,
        // which it comes down through)
        const room = radius + JumpIn.settleClearance;
        if (ground === "glass" || (landable(ground) && landable(groundAt(aim, room, roofs, solids, terrain, tops)))) {
            this._settle = undefined;
            return { aim, ground, spot: aim, settling: false };
        }
        // the chute's reach in its final approach: from now, in it; before it, from where it'll be as it starts
        const before = Math.max(0, left - JumpIn.settleMs);
        const reach = before > 0
            ? settleReach(landingGuess(position, velocity, direction, before), steer(velocity, direction, before), left - before)
            : settleReach(position, velocity, left);
        const last = this._settle;
        if (last && (last.beyond || last.spot === undefined || inReach({ center: reach.center, radius: reach.radius / 0.9 + 1 }, last.spot))) {
            const moved = Vec.len(Vec.sub(aim, last.aim));
            const onTheWay = last.spot !== undefined && moved + Vec.len(Vec.sub(last.spot, aim)) <= Vec.len(Vec.sub(last.spot, last.aim)) + 1.5;
            const still = last.spot === undefined || landable(groundAt(last.spot, room, roofs, solids, terrain, tops));
            if ((moved < 2 || onTheWay || now - last.at < this._searchMs) && still) return { aim, ground, spot: last.spot ?? aim, settling: true };
        }
        // (only the roofs the search could get to)
        const out = JumpIn.landingSearch + radius + 2;
        const area = new RectangleHitbox(
            Vec(Math.min(aim.x, reach.center.x) - out, Math.min(aim.y, reach.center.y) - out),
            Vec(Math.max(aim.x, reach.center.x) + out, Math.max(aim.y, reach.center.y) + out)
        );
        const near = roofs.filter(roof => roof.bounds.collidesWith(area));
        const nearTops: RoofTops = {
            decks: tops.decks.filter(deck => deck.bounds.collidesWith(area)),
            glass: tops.glass.filter(pane => pane.toRectangle().collidesWith(area)),
            solids: tops.solids
        };
        const open = (point: Vector): boolean => landable(groundAt(point, room, near, solids, terrain, nearTops));
        const inside = nearestOpen(aim, room, width, height, point => inReach(reach, point) && open(point), reach.center);
        const spot = inside ?? nearestOpen(reach.center, room, width, height, open);
        this._settle = { aim: Vec.clone(aim), spot, at: now, beyond: inside === undefined };
        return { aim, ground, spot: spot ?? aim, settling: true };
    }
}

/**
 * A chute's drift as its page sees it (units a ms), for the ring: over the last `windowMs` or so of its updates, as they
 * came (1.06.1's second review: worked out from each update and the time since the one before, on a busy connection,
 * updates bunched and spread, it was off by half and more, and the ring's aim with it). A jump in position (a landing
 * pushed off a roof, a teleport) isn't a drift: it starts again from there.
 */
export class Drift {
    private readonly _seen: Array<{ readonly at: number, readonly position: Vector }> = [];
    private _velocity = Vec(0, 0);

    constructor(private readonly _windowMs: number, private readonly _tickMs: number) {}

    get velocity(): Vector {
        return this._velocity;
    }

    reset(): void {
        this._seen.length = 0;
        this._velocity = Vec(0, 0);
    }

    /** an update's position, come at `now` (ms) */
    note(now: number, position: Vector): void {
        const seen = this._seen;
        const last = seen[seen.length - 1];
        if (last && Vec.len(Vec.sub(position, last.position)) > JumpIn.glideSpeed * 2 * Math.max(now - last.at, this._tickMs) + 1) seen.length = 0;
        seen.push({ at: now, position: Vec.clone(position) });
        while (seen.length > 2 && now - seen[1].at >= this._windowMs) seen.shift();
        const first = seen[0];
        const span = now - first.at;
        if (seen.length < 2 || span < this._windowMs / 2) return;
        const drift = Vec.scale(Vec.sub(position, first.position), 1 / span);
        if (Vec.len(drift) < JumpIn.glideSpeed * 2) this._velocity = drift;
    }
}
